import {Loader2} from 'lucide-react';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {Checkbox} from '@common-web/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import type {CatalogProduct} from '@client/hooks/useProductSelection';

type CoverageProductsStepProps = {
  urlGroupId: string | undefined;
  groupId: string;
  coverageEffectiveDate: string;
  onCoverageEffectiveDateChange: (value: string) => void;
  // Product selection at import time is Champ-only; other tenants assign
  // products through the analyzer flow after the members exist in VBA.
  showProductSelection: boolean;
  initialProductId: string;
  onInitialProductChange: (value: string) => void;
  additionalProductIds: string[];
  onToggleAdditionalProduct: (productId: string) => void;
  productCatalog: CatalogProduct[];
  remainingProducts: CatalogProduct[];
  allSelectedProductIds: string[];
  getProductName: (id: string) => string;
  isLoadingProducts: boolean;
  fileName: string | undefined;
  totalRows: number;
  canProceed: boolean;
  onImport: () => void;
  isImporting: boolean;
  onBack: () => void;
};

export function CoverageProductsStep({
  urlGroupId,
  groupId,
  coverageEffectiveDate,
  onCoverageEffectiveDateChange,
  showProductSelection,
  initialProductId,
  onInitialProductChange,
  additionalProductIds,
  onToggleAdditionalProduct,
  productCatalog,
  remainingProducts,
  allSelectedProductIds,
  getProductName,
  isLoadingProducts,
  fileName,
  totalRows,
  canProceed,
  onImport,
  isImporting,
  onBack,
}: CoverageProductsStepProps) {
  return (
    <div className='space-y-6'>
      <Card className='border-0 shadow-lg bg-white overflow-hidden'>
        <CardHeader className='bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4'>
          <CardTitle className='text-lg font-semibold text-brand-navy'>
            Step 5: {showProductSelection ? 'Coverage & Products' : 'Coverage'}
          </CardTitle>
        </CardHeader>
        <CardContent className='p-6 space-y-4'>
          {urlGroupId ? (
            <div className='flex items-center gap-2 p-3 rounded-lg bg-gray-50'>
              <span className='text-sm font-medium text-gray-500'>Group:</span>
              <span className='text-sm font-semibold text-brand-navy'>{urlGroupId}</span>
            </div>
          ) : (
            <div className='flex items-center gap-2 p-3 rounded-lg bg-gray-50'>
              <span className='text-sm font-medium text-gray-500'>Group:</span>
              <span className='text-sm font-semibold text-brand-navy'>{groupId}</span>
            </div>
          )}

          <div className='space-y-2'>
            <Label htmlFor='coverageEffectiveDate'>Coverage Effective Date *</Label>
            <div className='max-w-xs'>
              <Input
                id='coverageEffectiveDate'
                type='date'
                value={coverageEffectiveDate}
                onChange={(e) => onCoverageEffectiveDateChange(e.target.value)}
              />
            </div>
          </div>

          {showProductSelection && (
            <div className='space-y-2'>
              <Label htmlFor='initialProduct'>Initial Product *</Label>
              <div className='max-w-sm'>
                <Select value={initialProductId} onValueChange={onInitialProductChange}>
                  <SelectTrigger id='initialProduct'>
                    <SelectValue placeholder='Select product...' />
                  </SelectTrigger>
                  <SelectContent>
                    {productCatalog.map((p: CatalogProduct) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                        {p.productId ? ` (${p.productId})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {isLoadingProducts && <p className='text-xs text-gray-400'>Loading products…</p>}
            </div>
          )}
        </CardContent>
      </Card>

      {initialProductId && remainingProducts.length > 0 && (
        <Card className='border-0 shadow-lg bg-white overflow-hidden'>
          <CardHeader className='bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4'>
            <CardTitle className='text-lg font-semibold text-brand-navy'>
              Additional Products
            </CardTitle>
          </CardHeader>
          <CardContent className='p-6'>
            <div className='space-y-2'>
              {remainingProducts.map((p: CatalogProduct) => (
                <label
                  key={p.id}
                  className='flex items-center gap-3 p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors'
                >
                  <Checkbox
                    checked={additionalProductIds.includes(p.id)}
                    onCheckedChange={() => onToggleAdditionalProduct(p.id)}
                  />
                  <div>
                    <p className='text-sm font-medium text-brand-navy'>{p.name}</p>
                    {p.productId && <p className='text-xs text-gray-500'>{p.productId}</p>}
                  </div>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {(!showProductSelection || allSelectedProductIds.length > 0) && (
        <Card className='border-0 shadow-lg bg-white overflow-hidden'>
          <CardHeader className='bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4'>
            <CardTitle className='text-lg font-semibold text-brand-navy'>Import Summary</CardTitle>
          </CardHeader>
          <CardContent className='p-6'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <div>
                <h4 className='text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3'>
                  File
                </h4>
                <div className='space-y-1.5 text-sm'>
                  <p>
                    <span className='text-gray-400'>File:</span> {fileName}
                  </p>
                  <p>
                    <span className='text-gray-400'>Members:</span> {totalRows}
                  </p>
                  <p>
                    <span className='text-gray-400'>Coverage Effective:</span>{' '}
                    {coverageEffectiveDate}
                  </p>
                </div>
              </div>
              {allSelectedProductIds.length > 0 && (
                <div>
                  <h4 className='text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3'>
                    Products
                  </h4>
                  <div className='space-y-2'>
                    {allSelectedProductIds.map((id) => (
                      <div key={id} className='flex items-center p-2 rounded-lg bg-gray-50'>
                        <span className='text-sm font-medium text-brand-navy'>
                          {getProductName(id)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <div className='flex items-center justify-between'>
        <Button variant='outline' onClick={onBack}>
          Back
        </Button>
        <div className='flex items-center gap-3'>
          {!canProceed && (
            <span className='text-sm text-red-600'>
              {showProductSelection
                ? 'Coverage date and initial product are required'
                : 'Coverage date is required'}
            </span>
          )}
          <Button onClick={onImport} disabled={!canProceed || isImporting}>
            {isImporting && <Loader2 className='h-4 w-4 mr-2 animate-spin' />}
            Import {totalRows} Member{totalRows !== 1 ? 's' : ''}
          </Button>
        </div>
      </div>
    </div>
  );
}
